import React from 'react';
import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  typography,
} from '../../style-utils/theme';
import { touch } from '../../style-utils/media-queries';
import { SCALE } from '../../motion';

/**
 * THE TOGGLE CHIP — a native button that says whether it is on.
 *
 * Styled from the dashboard's RangeChip (a pill, 32px at a mouse, 44px under a
 * thumb, a tint fill and a brand border when on) but with the one thing that
 * chip does not carry: `aria-pressed`. A range chip is one of a set and its
 * selection is read from the data; a toggle chip is a standalone on/off, and
 * without aria-pressed a screen reader hears a button that does nothing
 * visible.
 *
 * A NATIVE <button type="button">, so Space and Enter toggle it for free, it is
 * in the tab order, and it never submits the form it sits in.
 *
 * Single-select sets (a category) are a ChipGroup of these where the caller
 * presses one and releases the rest; multi-select sets (weekdays) press any.
 */

type ChipStyleProps = { $pressed: boolean };

const chipColor = (p: ChipStyleProps) => {
  return p.$pressed ? palette.irisDeep : palette.slate;
};
const chipBg = (p: ChipStyleProps) => {
  return p.$pressed ? palette.irisTint : palette.surface;
};
const chipBorder = (p: ChipStyleProps) => {
  return p.$pressed ? palette.iris : palette.line;
};
// A pressed chip keeps its fill under the pointer: washing it back to the
// hover tint made "on" look like "off" for as long as the mouse sat on it.
const chipHoverBg = (p: ChipStyleProps) => {
  return p.$pressed ? palette.irisTint : palette.irisTintSoft;
};

/* The label may wrap, but only when it cannot fit at all: flex-basis auto is
   the label's own width, so a short label stays on one line, and max-width
   stops a long one pushing the page sideways at 320. */
const Chip = styled.button<ChipStyleProps>`
  -webkit-appearance: none;
  appearance: none;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  min-height: ${layout.compactControlHeight};
  padding: ${spacing.half} ${spacing.sm};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  line-height: ${typography.leading.snug};
  text-align: center;
  overflow-wrap: anywhere;
  color: ${chipColor};
  background: ${chipBg};
  border: 1px solid ${chipBorder};
  border-radius: ${radius.pill};
  cursor: pointer;
  transition: color ${duration.fast} ${easing.standard},
    background-color ${duration.fast} ${easing.standard},
    border-color ${duration.fast} ${easing.standard},
    box-shadow ${duration.base} ${easing.standard},
    transform ${duration.instant} ${easing.spring};

  &:hover:not(:disabled) {
    border-color: ${palette.lagoon};
    background: ${chipHoverBg};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: scale(${SCALE.pressed});
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }

  &:disabled {
    cursor: not-allowed;
    color: ${palette.slateDisabled};
    background: ${palette.surfaceMuted};
    border-color: ${palette.line};
  }

  ${touch} {
    min-height: ${layout.touchTarget};
  }
`;

export interface ToggleChipProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'onToggle' | 'onClick' | 'aria-pressed' | 'children'
  > {
  pressed: boolean;
  /** Called with the state the chip should move TO. */
  onToggle: (next: boolean) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export const ToggleChip = React.forwardRef<HTMLButtonElement, ToggleChipProps>(
  ({ pressed, onToggle, children, disabled = false, ...rest }, ref) => (
    <Chip
      ref={ref}
      type="button"
      aria-pressed={pressed}
      disabled={disabled}
      $pressed={pressed}
      onClick={() => onToggle(!pressed)}
      {...rest}
    >
      {children}
    </Chip>
  )
);
ToggleChip.displayName = 'ToggleChip';

/**
 * A LABELLED SET OF CHIPS.
 *
 * `role="group"` with a required accessible name, so a screen reader announces
 * "Category, group" before the first chip rather than a loose run of buttons.
 *
 * It WRAPS. It never scrolls sideways: inside a form a hidden overflow is a
 * choice nobody can see, and the dashboard's scrolling chip strip is a header
 * pattern, not a field.
 */
const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs};
  min-width: 0;
  max-width: 100%;
`;

export interface ChipGroupProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'role' | 'aria-label'> {
  'aria-label': string;
  children: React.ReactNode;
}

export const ChipGroup: React.FC<ChipGroupProps> = ({ children, ...rest }) => (
  <Group role="group" {...rest}>
    {children}
  </Group>
);
